// =============================================================================
// Wizard — intercepts step-1 / step-2 form navigation and saves state to
// sessionStorage before the page transitions. Keeps all existing prototype
// behaviours (GAHT drawer, life-stage reveal, cross-fade) intact — this
// module only adds a capture step on the "Next" / "Read my results" link.
// =============================================================================

import {
  clear as clearStoredInputs,
  readStep1,
  readStep2,
  restoreStep1,
  restoreStep2,
  saveStep1,
  saveStep2,
} from './form-state.js';

/** Called on step-1. Captures form state when the user clicks "Next". */
function bindStep1Capture(): void {
  const nextBtn = document.querySelector<HTMLAnchorElement>('[data-submit-btn]');
  if (!nextBtn) return;
  nextBtn.addEventListener(
    'click',
    (e) => {
      // If required fields are missing, scripts.js keeps the button disabled
      // and the anchor will still fire. Bail early so we don't overwrite
      // valid state with partial data.
      if (nextBtn.classList.contains('is-disabled') || (nextBtn as unknown as HTMLButtonElement).disabled) {
        return;
      }
      const snap = readStep1();
      if (snap) saveStep1(snap);
      // If readStep1 returned null (missing required field), let the page
      // transition happen anyway — user will hit the guard on results.html.
      // Do not preventDefault; the prototype's fade-transition still runs.
      void e;
    },
    { capture: true },
  );
}

/** Called on step-2. Captures form state when the user clicks "Read my results". */
function bindStep2Capture(): void {
  const nextBtn = document.querySelector<HTMLAnchorElement>('[data-step2-btn]');
  if (!nextBtn) return;
  nextBtn.addEventListener(
    'click',
    (e) => {
      if (nextBtn.classList.contains('is-disabled') || (nextBtn as unknown as HTMLButtonElement).disabled) {
        return;
      }
      const snap = readStep2();
      saveStep2(snap);
      void e;
    },
    { capture: true },
  );
}

/**
 * If the user is entering step-1 fresh (referrer is empty or another non-
 * wizard page like the landing page or methodology), clear any stale
 * sessionStorage from a previous test run or from a dev preview page that
 * may have seeded sample values. Without this, a returning user sees their
 * previous inputs auto-restored when they intend a fresh start, and a user
 * who hit a dev preview page first sees seeded sample data in step 2.
 *
 * "Internal wizard navigation" (back-button from step-2 to step-1, or arriving
 * at step-1 from results / pdf-preview) is preserved so the user does not
 * lose in-flight state when navigating between steps.
 */
function clearStaleStateOnFreshEntry(): void {
  // Only run on step-1.
  if (!document.querySelector('[data-submit-btn]')) return;
  const referrer = document.referrer || '';
  const fromInternalWizard =
    referrer.includes('/step-1') ||
    referrer.includes('/step-2') ||
    referrer.includes('/results') ||
    referrer.includes('/pdf-preview');
  if (fromInternalWizard) return;
  clearStoredInputs();
  try {
    sessionStorage.removeItem('ff-pdf-unlocked');
  } catch {
    // ignore
  }
}

/**
 * Populate the form from sessionStorage on page load. Detects whether we're
 * on step-1 or step-2 by looking for the step-specific submit button. A fresh
 * visit with empty sessionStorage is a no-op.
 */
function restoreOnLoad(): void {
  if (document.querySelector('[data-submit-btn]')) {
    restoreStep1();
  } else if (document.querySelector('[data-step2-btn]')) {
    restoreStep2();
  }
}

function init(): void {
  clearStaleStateOnFreshEntry();
  restoreOnLoad();
  bindStep1Capture();
  bindStep2Capture();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
